import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, RefreshCw, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import InfoTip from './InfoTip';
import {
  APPEARANCE_ACCENTS,
  APPEARANCE_FONTS,
  APPEARANCE_PALETTES,
  APPEARANCE_SURFACES,
  DEFAULT_APPEARANCE,
  announceAppearance,
  applyAppearance,
  normalizeAppearance,
} from '../lib/appearance';

const PREVIEW_RADIUS = { soft: 16, balanced: 10, sharp: 4 };

const AppearanceSettings = forwardRef(function AppearanceSettings(
  { showSaveAction = true, onStatusChange },
  ref,
) {
  const [settings, setSettings] = useState(DEFAULT_APPEARANCE);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const savedSettings = useRef(DEFAULT_APPEARANCE);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    axiosInstance.get('/users/me/appearance')
      .then(({ data }) => {
        if (!active) return;
        const normalized = normalizeAppearance(data);
        savedSettings.current = normalized;
        setSettings(normalized);
        applyAppearance(normalized);
      })
      .catch((error) => {
        if (active) setLoadError(error.response?.data?.detail || 'Theme settings could not be loaded.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      applyAppearance(savedSettings.current);
    };
  }, [loadAttempt]);

  const selectedPalette = useMemo(
    () => APPEARANCE_PALETTES.find(({ id }) => id === settings.palette) || APPEARANCE_PALETTES[0],
    [settings.palette]
  );
  const selectedAccent = useMemo(
    () => APPEARANCE_ACCENTS.find(({ id }) => id === settings.accent) || APPEARANCE_ACCENTS[0],
    [settings.accent]
  );
  const selectedFont = useMemo(
    () => APPEARANCE_FONTS.find(({ id }) => id === settings.font) || APPEARANCE_FONTS[0],
    [settings.font]
  );
  const dirty = JSON.stringify(normalizeAppearance(settings)) !== JSON.stringify(savedSettings.current);

  useEffect(() => {
    onStatusChange?.({ ready: !loading && !loadError, dirty, saving });
  }, [dirty, loadError, loading, onStatusChange, saving]);

  const preview = (changes) => {
    const next = normalizeAppearance({ ...settings, ...changes });
    setSettings(next);
    applyAppearance(next);
  };

  const restoreDefault = () => preview(DEFAULT_APPEARANCE);

  const persist = async ({ notify = true } = {}) => {
    setSaving(true);
    try {
      const payload = normalizeAppearance(settings);
      const { data } = await axiosInstance.put('/users/me/appearance', payload);
      const saved = normalizeAppearance(data);
      savedSettings.current = saved;
      setSettings(saved);
      announceAppearance(saved);
      if (notify) toast.success('Your theme was updated');
      return true;
    } catch (error) {
      if (notify) {
        toast.error(error.response?.data?.detail || 'Failed to save theme settings');
        return false;
      }
      throw error;
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    save: () => persist({ notify: false }),
    isDirty: () => dirty,
    isReady: () => !loading && !loadError,
  }), [dirty, loadError, loading, settings]);

  const retryLoad = () => {
    setLoading(true);
    setLoadError('');
    setLoadAttempt((attempt) => attempt + 1);
  };

  if (loading) return <Card className="p-8 text-center text-sm text-slate-500">Loading your themes…</Card>;

  if (loadError) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-6" data-testid="appearance-settings-load-error">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-amber-600" size={20} />
          <div className="flex-1">
            <h2 className="font-semibold text-amber-950">Themes are unavailable</h2>
            <p className="mt-1 text-sm text-amber-800">{loadError} Retry before changing your theme.</p>
            <Button type="button" variant="outline" className="mt-4 border-amber-300 bg-white text-amber-950" onClick={retryLoad}>
              <RefreshCw size={16} /> Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Dark themes use each accent's bright shade so text on it stays readable.
  const accentShade = (accent) => (selectedPalette.dark ? accent.bright : accent.color);
  const previewAccent = accentShade(selectedAccent);
  const previewRadius = PREVIEW_RADIUS[settings.surface_style] || 10;
  const controlRadius = Math.min(previewRadius, 8);
  const previewMenuText = selectedPalette.sidebar === '#0f172a' ? '#ffffff' : selectedPalette.text;

  return (
    <div className="space-y-6" data-testid="appearance-settings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-bold">Themes</h2>
          <InfoTip label="About themes">
            Choose how Qflow looks for your account. Changes preview immediately and affect only you after saving.
            These choices are private to your Qflow account and do not change another user's view.
          </InfoTip>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={restoreDefault} disabled={saving}><RotateCcw size={15} />Restore default</Button>
      </div>

      <div className="flex flex-wrap items-start gap-6">
        <div className="min-w-0 flex-[999_1_420px] space-y-7">
          <section aria-labelledby="background-heading" className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 id="background-heading" className="text-[15px] font-bold">Theme</h3>
              <InfoTip label="About theme">Sets the background, panels and menu colours together. Four light and two dark themes.</InfoTip>
            </div>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(150px,1fr))]">
              {APPEARANCE_PALETTES.map((palette) => {
                const selected = settings.palette === palette.id;
                return (
                  <button key={palette.id} type="button" aria-pressed={selected} onClick={() => preview({ palette: palette.id })} disabled={saving} className="qflow-option relative flex flex-col gap-2 rounded-xl p-2 text-left disabled:cursor-not-allowed disabled:opacity-60" data-testid={`appearance-palette-${palette.id}`}>
                    <span className="flex h-14 overflow-hidden rounded-lg border" style={{ borderColor: palette.border }}>
                      <span className="w-[30%]" style={{ background: palette.sidebar, borderRight: `1px solid ${palette.border}` }} />
                      <span className="flex-1 p-2" style={{ background: palette.background }}><span className="block h-full rounded" style={{ background: palette.surface }} /></span>
                    </span>
                    <span className="px-0.5 pb-0.5">
                      <span className="block text-[13px] font-bold">{palette.name}</span>
                      <span className="block text-xs opacity-75">{palette.tone}</span>
                    </span>
                    {selected && <span className="qflow-accent-bg absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full"><Check size={12} /></span>}
                  </button>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="accent-heading" className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 id="accent-heading" className="text-[15px] font-bold">Accent</h3>
              <span className="text-sm opacity-75">{selectedAccent.name}</span>
              <InfoTip label="About accent colours">Used for navigation, primary actions, active controls and focus states. Each accent has a shade for light themes and one for dark themes, so text on it stays readable.</InfoTip>
            </div>
            <div className="flex flex-wrap gap-4">
              {APPEARANCE_ACCENTS.map((accent) => {
                const selected = settings.accent === accent.id;
                const shade = accentShade(accent);
                return <button key={accent.id} type="button" aria-label={accent.name} aria-pressed={selected} onClick={() => preview({ accent: accent.id })} disabled={saving} className="qflow-swatch h-9 w-9 rounded-full disabled:cursor-not-allowed disabled:opacity-60" style={{ background: shade, '--qflow-swatch-color': shade }} data-testid={`appearance-accent-${accent.id}`} />;
              })}
            </div>
          </section>

          <section aria-labelledby="font-heading" className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 id="font-heading" className="text-[15px] font-bold">Font</h3>
              <InfoTip label="About fonts">All four are chosen for long answers and dense tables. Manrope is the Qflow default.</InfoTip>
            </div>
            <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(130px,1fr))]">
              {APPEARANCE_FONTS.map((font) => {
                const selected = settings.font === font.id;
                return (
                  <button key={font.id} type="button" aria-pressed={selected} onClick={() => preview({ font: font.id })} disabled={saving} className="qflow-option rounded-xl px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60" data-testid={`appearance-font-${font.id}`}>
                    <span className="block text-2xl font-bold" style={{ fontFamily: font.family }}>Aa</span>
                    <span className="block text-[13px] font-semibold" style={{ fontFamily: font.family }}>{font.name}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="surface-heading" className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 id="surface-heading" className="text-[15px] font-bold">Corners</h3>
              <InfoTip label="About corners">How rounded cards, buttons and fields look.</InfoTip>
            </div>
            <div role="group" aria-labelledby="surface-heading" className="inline-flex gap-1 rounded-xl border p-1" style={{ borderColor: 'var(--qflow-border)', background: 'var(--qflow-surface)' }}>
              {APPEARANCE_SURFACES.map((surface) => {
                const selected = settings.surface_style === surface.id;
                return <button key={surface.id} type="button" aria-pressed={selected} title={surface.description} onClick={() => preview({ surface_style: surface.id })} disabled={saving} className="qflow-segment h-9 rounded-lg px-4 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-60" data-testid={`appearance-surface-${surface.id}`}>{surface.name}</button>;
              })}
            </div>
          </section>
        </div>

        <aside className="min-w-0 max-w-[420px] flex-[1_1_320px] space-y-3" data-testid="appearance-live-preview">
          <h3 className="text-[15px] font-bold">Preview</h3>
          <div className="flex h-[300px] overflow-hidden border text-[13px]" style={{ borderRadius: 14, borderColor: selectedPalette.border, background: selectedPalette.background, color: selectedPalette.text, fontFamily: selectedFont.family }}>
            <div className="flex w-[104px] flex-none flex-col gap-1 p-2" style={{ background: selectedPalette.sidebar, borderRight: `1px solid ${selectedPalette.border}`, color: previewMenuText }}>
              <span className="mb-2 flex items-center gap-1.5 font-extrabold"><span className="qflow-brand-mark h-5 w-5 text-[11px]" style={{ borderRadius: 6 }}>Q</span>Qflow</span>
              <span className="rounded-md px-2 py-1.5 font-bold" style={{ background: `color-mix(in srgb, ${previewAccent} 16%, transparent)`, color: selectedPalette.sidebar === '#0f172a' ? '#ffffff' : previewAccent }}>Home</span>
              <span className="px-2 py-1.5 opacity-75">Knowledge</span>
              <span className="px-2 py-1.5 opacity-75">People</span>
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-3 p-3">
              <div className="border p-3" style={{ borderRadius: previewRadius, borderColor: selectedPalette.border, background: selectedPalette.surface }}>
                <span className="block font-bold">What is the approved next step?</span>
                <span className="mt-1.5 block leading-relaxed opacity-80">Follow the approved process and retain the cited evidence.</span>
                <span className="mt-2 block font-bold" style={{ color: previewAccent }}>Grounded answer</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 items-center px-3.5 font-bold" style={{ borderRadius: controlRadius, background: previewAccent, color: selectedPalette.dark ? '#16102a' : '#ffffff' }}>Ask Qflow</span>
                <span className="inline-flex h-8 items-center border px-3 font-semibold" style={{ borderRadius: controlRadius, borderColor: selectedPalette.border, background: selectedPalette.surface }}>Cancel</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {showSaveAction && dirty && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border p-3 pl-4 shadow-lg" style={{ borderColor: 'var(--qflow-border)', background: 'var(--qflow-surface)' }} data-testid="appearance-unsaved-bar">
          <span className="font-semibold">You have unsaved changes</span>
          <span className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={() => preview(savedSettings.current)} disabled={saving}>Discard</Button>
            <Button type="button" onClick={() => persist()} disabled={saving || !dirty}><Save size={16} />{saving ? 'Saving…' : 'Save'}</Button>
          </span>
        </div>
      )}
    </div>
  );
});

export default AppearanceSettings;
